import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export const PurchaseModal = ({ pkg, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [mode, setMode] = useState("online");
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const open = !!pkg;
  const hasModes = !!(pkg && pkg.online && pkg.offline);

  useEffect(() => { if (pkg) { setMode("online"); } }, [pkg]);

  const variant = hasModes ? pkg[mode] : pkg;
  const total = variant ? variant.total : "";
  const per = variant ? variant.per : "";
  const packageKey = pkg ? (hasModes ? `${pkg.key}_${mode}` : pkg.key) : "";

  const close = () => { onClose(); setTimeout(() => { setDone(false); setConsent(false); setMode("online"); setForm({ name: "", email: "", phone: "", note: "" }); }, 300); };

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (!/^\d{7,15}$/.test(form.phone.replace(/\s/g, ""))) return toast.error("Please enter a valid phone number");
    if (!consent) return toast.error("Please accept the eligibility note to proceed");
    setLoading(true);
    try {
      const res = await api.post("/packages/purchase", { package_key: packageKey, consent, ...form, phone: form.phone.replace(/\s/g, "") });
      const { purchase, order_id, key_id, amount } = res.data;
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment gateway."); return; }
      setLoading(false);
      const rzp = new window.Razorpay({
        key: key_id, amount, currency: "INR", order_id,
        name: "Serene Mind Place", description: `${pkg.name}${hasModes ? ` · ${mode}` : ""}`,
        prefill: { name: form.name, email: form.email, contact: form.phone.replace(/\s/g, "") },
        theme: { color: "#D98BA0" },
        handler: async (resp) => {
          setLoading(true);
          try { await api.post("/packages/verify", { purchase_id: purchase.id, ...resp }); setDone(true); toast.success("Payment received — package booked!"); }
          catch { toast.error("Payment verification failed — please contact us on 083176 34326."); }
          finally { setLoading(false); }
        },
        modal: { ondismiss: () => toast("Payment cancelled.") },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4 bg-[#20301A]/50 backdrop-blur-sm" onClick={close}>
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
            data-testid="purchase-modal"
            className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-[28px] bg-[#F2F8E8] soft-shadow p-6 sm:p-8 relative">
            <button onClick={close} data-testid="purchase-close" className="absolute top-5 right-5 text-[#55703F] hover:text-[#20301A]"><X size={20} /></button>

            {done ? (
              <div className="py-10 text-center" data-testid="purchase-success">
                <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-[#D98BA0] text-white"><Check size={30} /></div>
                <h3 className="font-serif text-3xl mt-5 text-[#20301A]">Package booked</h3>
                <p className="text-[#55703F] mt-3 max-w-sm mx-auto">Thank you, {form.name.split(" ")[0]}. We've emailed your receipt to <b>{form.email}</b> and will reach out to schedule your first session.</p>
                <button onClick={close} className="mt-7 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-7 py-3 text-sm hover:bg-[#20301A] transition-colors">Done</button>
              </div>
            ) : (
              <>
                <span className="overline text-[#C77D94]">Buy therapy package</span>
                <h3 className="font-serif text-2xl sm:text-3xl mt-2 text-[#20301A]">{pkg.name}</h3>
                <p className="mt-1 text-sm text-[#55703F]">{pkg.sessions}</p>

                {hasModes && (
                  <div className="mt-4 inline-flex rounded-full border border-[#C9DFAC] bg-white p-1 w-full">
                    {["online", "offline"].map((m) => (
                      <button key={m} data-testid={`purchase-mode-${m}`} onClick={() => setMode(m)}
                        className={`flex-1 rounded-full px-4 py-2 text-xs capitalize transition-all ${mode === m ? "bg-[#D98BA0] text-white" : "text-[#55703F]"}`}>
                        {m} · {pkg[m].total}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex items-end gap-3">
                  <span className="font-serif text-4xl text-[#D98BA0]" data-testid="purchase-total">{total}</span>
                  <span className="text-sm text-[#55703F] pb-1.5">({per} per session)</span>
                </div>

                <div className="mt-5 space-y-3">
                  <input data-testid="purchase-name-input" placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0]" />
                  <input data-testid="purchase-email-input" type="email" placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0]" />
                  <input data-testid="purchase-phone-input" placeholder="Phone number" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0]" />
                  <textarea data-testid="purchase-note-input" rows={2} placeholder="Anything you'd like us to know? (optional)" value={form.note} onChange={(e) => set("note", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0] resize-none" />
                </div>

                <div data-testid="purchase-warning" className="mt-5 flex items-start gap-3 rounded-2xl bg-[#FFEFDF] border border-[#FFC9A0] px-4 py-3">
                  <AlertTriangle size={18} className="text-[#B0500F] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#7a4a1a] leading-relaxed">
                    Please note: this package becomes eligible to use <span className="font-semibold">only after your regular first consultation session is complete</span>. Your sessions will be scheduled after that.
                  </p>
                </div>
                <label data-testid="purchase-consent" className="mt-3 flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" data-testid="purchase-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#D98BA0]" />
                  <span className="text-xs text-[#3C5A2E] leading-relaxed">Yes, I agree — I understand this package can be used only after my first consultation.</span>
                </label>

                <button data-testid="purchase-submit-button" onClick={submit} disabled={loading || !consent}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D98BA0] text-white px-6 py-3.5 text-sm hover:bg-[#c2748c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Processing…</> : <>Pay {total} & Book <Check size={16} /></>}
                </button>
                <p className="mt-3 text-xs text-[#55703F] text-center">Secure payment via Razorpay · We'll contact you to schedule</p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
